"use client";

import React from "react";
import { Shield, FileCheck, Lock, TriangleAlert, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const strategies = [{
  instrument: "Vendor Single Interest (VSI)",
  coverage: "Physical Damage & Theft",
  mechanism: "Blanket Portfolio Policy",
  assetClass: "Auto, Equipment, Consumer Goods",
  description: "Eliminates tracking requirements while insuring collateral against physical damage and theft upon repossession."
}, {
  instrument: "Lender Single Interest (LSI)",
  coverage: "Default & Collateral Loss",
  mechanism: "Master Policy Structure",
  assetClass: "Real Estate, Vehicle Fleets",
  description: "Protects the lender's interest in collateral against damage, non-filing errors, and confiscation."
}, {
  instrument: "Collateral Protection (CPI)",
  coverage: "Uninsured Collateral Risk",
  mechanism: "Force-Placed Coverage",
  assetClass: "Mortgages, Auto Loans",
  description: "Automated tracking and placement of insurance on collateral when a borrower fails to maintain required coverage."
}, {
  instrument: "Credit Life & Disability",
  coverage: "Borrower Mortality/Morbidity",
  mechanism: "Debt Cancellation Contract",
  assetClass: "Consumer & Commercial Loans",
  description: "Pays off or reduces the loan balance in the event of the borrower's death or disability, securing the asset."
}, {
  instrument: "GAP Waivers",
  coverage: "Deficiency Balances",
  mechanism: "Addendum to Loan/Lease",
  assetClass: "Vehicles, Heavy Equipment",
  description: "Covers the difference between the asset's actual cash value and the outstanding loan balance in a total loss event."
}];
export default function ProtectionStrategies() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: tableRef,
    isVisible: isTableVisible
  } = useRevealOnScroll();
  return <section id="protection-strategies" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-20" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-20" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div ref={headerRef} className={cn("max-w-3xl mb-16 transition-all duration-700 ease-out transform", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-primary" />
            <span className="text-sm font-mono uppercase tracking-widest text-muted-foreground">
              Risk Mitigation
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-light tracking-tight text-foreground mb-6">
            Institutional Protection Strategies
          </h2>
          <p className="text-lg text-muted-foreground font-light leading-relaxed">
            Safeguard capital and collateral with structured insurance instruments designed for high-volume lending portfolios. Our strategies minimize exposure while maximizing operational efficiency.
          </p>
        </div>

        {}
        <div ref={tableRef} className={cn("w-full transition-all duration-700 delay-200 ease-out transform", isTableVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="rounded-sm border border-border bg-card/50 backdrop-blur-sm overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/20">
                <TableRow className="border-b border-border hover:bg-transparent">
                  <TableHead className="w-[250px] text-xs uppercase tracking-wider font-mono text-muted-foreground py-6">
                    Instrument
                  </TableHead>
                  <TableHead className="w-[200px] text-xs uppercase tracking-wider font-mono text-muted-foreground py-6">
                    Risk Coverage
                  </TableHead>
                  <TableHead className="w-[200px] text-xs uppercase tracking-wider font-mono text-muted-foreground py-6 hidden md:table-cell">
                    Mechanism
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-mono text-muted-foreground py-6 hidden lg:table-cell">
                    Strategic Application
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {strategies.map((strategy, i) => <TableRow key={strategy.instrument} data-index={i} className="group border-b border-border/40 hover:bg-white/[0.02] transition-colors duration-300">
                    <TableCell className="font-medium text-foreground py-6 align-top">
                      <div className="flex items-start gap-3">
                        <FileCheck className="w-4 h-4 text-primary mt-1 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" />
                        <span className="text-base">{strategy.instrument}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground py-6 align-top">
                      <div className="flex items-start gap-2">
                        <TriangleAlert className="w-3.5 h-3.5 mt-1 shrink-0 opacity-50" />
                        {strategy.coverage}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground py-6 align-top hidden md:table-cell">
                      <div className="flex items-start gap-2">
                        <Lock className="w-3.5 h-3.5 mt-1 shrink-0 opacity-50" />
                        {strategy.mechanism}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground py-6 align-top hidden lg:table-cell">
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-foreground/80">{strategy.assetClass}</p>
                        <p className="text-xs leading-relaxed opacity-70 max-w-md">
                          {strategy.description}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>

          {}
          <div className="mt-4 lg:hidden text-xs text-muted-foreground font-mono">
            * Scroll horizontally to view full details or contact us for specific structuring.
          </div>
        </div>

        {}
        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-border pt-8">
          <div>
            <h3 className="text-lg font-medium text-foreground mb-1">
              Need a Custom Risk Structure?
            </h3>
            <p className="text-sm text-muted-foreground">
              We engineer bespoke protection programs for institutional lenders and private equity firms.
            </p>
          </div>
          <Button asChild variant="default" size="lg" className="bg-foreground text-background hover:bg-foreground/90 rounded-none min-w-[200px]">
            <Link to="/ContactPage">
              Consult an Advisor <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}